import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import AppImage from '../../../../components/AppImage';

const ImageGallery = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(images?.[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : images?.length - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images?.[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex < images?.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setSelectedImage(images?.[newIndex]);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') closeLightbox();
    if (e?.key === 'ArrowLeft') goToPrevious();
    if (e?.key === 'ArrowRight') goToNext();
  };

  React.useEffect(() => {
    if (selectedImage) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage, currentIndex]);

  return (
    <>
      {/* Thumbnail Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images?.map((image, index) => (
          <button
            key={image?.id}
            onClick={() => openLightbox(index)}
            className="relative aspect-square overflow-hidden rounded-lg border border-gray-200 hover:border-blue-500 transition-all duration-200 hover:shadow-lg group"
          >
            <AppImage
              src={image?.imageUrl}
              alt={image?.imageAlt || `Review image ${index + 1}`}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
              <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">
                View Full Size
              </span>
            </div>
          </button>
        ))}
      </div>
      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-all"
            aria-label="Close lightbox"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation Buttons */}
          {images?.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 z-10 p-3 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-all"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 z-10 p-3 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-all"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 px-4 py-2 bg-white bg-opacity-20 rounded-full text-white text-sm font-medium">
            {currentIndex + 1} / {images?.length}
          </div>

          {/* Main Image */}
          <div className="max-w-7xl max-h-[90vh] px-4">
            <img
              src={selectedImage?.imageUrl}
              alt={selectedImage?.imageAlt || 'Full size review image'}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>

          {/* Image Description */}
          {selectedImage?.imageAlt && (
            <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-10 max-w-2xl px-4">
              <p className="text-white text-center text-sm bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                {selectedImage?.imageAlt}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ImageGallery;